/**
 * Session Invite Panel Component
 * Drawer/dialog for session owner to manage invites, search users, and share codes.
 */

import { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  CheckCircle as AcceptedIcon,
  Cancel as DeclinedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material'
import { useSessionInvites, useCreateInvite, useGenerateShareCode } from '../../hooks/useSessionInvites'
import { useQuery } from '@tanstack/react-query'
import { searchApi } from '@api/endpoints/search'
import { SearchEntityType, type UserSearchResult } from '@api/types/search'

interface SessionInvitePanelProps {
  open: boolean
  onClose: () => void
  sessionId: string
}

const INVITE_STATUS_CONFIG: Record<string, { label: string; icon: typeof PendingIcon; color: 'warning' | 'success' | 'error' | 'default' }> = {
  Pending: { label: 'Pending', icon: PendingIcon, color: 'warning' },
  Accepted: { label: 'Accepted', icon: AcceptedIcon, color: 'success' },
  Declined: { label: 'Declined', icon: DeclinedIcon, color: 'error' },
  Expired: { label: 'Expired', icon: PendingIcon, color: 'default' },
}

export default function SessionInvitePanel({ open, onClose, sessionId }: SessionInvitePanelProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [shareCode, setShareCode] = useState<string | null>(null)
  const [copySnackbar, setCopySnackbar] = useState(false)
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null)

  const { data: invites, isLoading: isLoadingInvites } = useSessionInvites(sessionId, open)
  const createInviteMutation = useCreateInvite(sessionId)
  const generateShareCodeMutation = useGenerateShareCode(sessionId)

  // Search users via global search endpoint (type=User)
  const { data: searchResponse, isLoading: isSearching } = useQuery({
    queryKey: ['user-search-invite', searchQuery],
    queryFn: () => searchApi.search({ query: searchQuery, type: SearchEntityType.User, take: 10 }),
    enabled: searchQuery.length >= 2 && open,
    staleTime: 1000,
  })

  const handleInviteUser = async (userId: string) => {
    try {
      await createInviteMutation.mutateAsync({ userId })
      setSearchQuery('')
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to send invite'
      setErrorSnackbar(message)
    }
  }

  const handleGenerateShareCode = async () => {
    try {
      const response = await generateShareCodeMutation.mutateAsync()
      setShareCode(response.shareCode)
    } catch {
      // Error handled by mutation state
    }
  }

  const handleCopyCode = () => {
    if (shareCode) {
      navigator.clipboard.writeText(shareCode)
      setCopySnackbar(true)
    }
  }

  const inviteList = Array.isArray(invites) ? invites : []
  const userResults: UserSearchResult[] = searchResponse?.users ?? []

  // Filter out already-invited users from search results
  const invitedUserIds = new Set(inviteList.map((i) => i.userId))
  const filteredResults = userResults.filter((u) => !invitedUserIds.has(u.userId))

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 360 } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon /> Invite Players
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search users to invite..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Search Results */}
            {searchQuery.length >= 2 && (
              <Box sx={{ mt: 1 }}>
                {isSearching ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : filteredResults.length > 0 ? (
                  <List dense disablePadding>
                    {filteredResults.slice(0, 5).map((user) => (
                      <ListItem key={user.userId} disablePadding sx={{ py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 36 }}>
                          <Avatar src={user.profilePicture || user.avatar} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                            {user.displayName?.[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={user.displayName}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleInviteUser(user.userId)}
                          disabled={createInviteMutation.isPending}
                        >
                          Invite
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No users found
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Share Code Section */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Share Code
            </Typography>
            {shareCode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={shareCode}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopyCode} size="small">
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ShareIcon />}
                onClick={handleGenerateShareCode}
                disabled={generateShareCodeMutation.isPending}
                fullWidth
              >
                {generateShareCodeMutation.isPending ? 'Generating...' : 'Generate Share Code'}
              </Button>
            )}
          </Box>

          <Divider />

          {/* Invite List */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Invites ({inviteList.length})
            </Typography>

            {isLoadingInvites ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : inviteList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No invites sent yet. Search for users above to invite them.
              </Typography>
            ) : (
              <List dense disablePadding>
                {inviteList.map((invite) => {
                  const config = INVITE_STATUS_CONFIG[invite.status] || INVITE_STATUS_CONFIG.Pending
                  return (
                    <ListItem key={invite.id} disablePadding sx={{ py: 0.5 }}>
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Avatar src={invite.userAvatarUrl || undefined} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                          {invite.userDisplayName?.[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={invite.userDisplayName || 'Unknown'}
                        secondary={invite.invitedByDisplayName ? `Invited by ${invite.invitedByDisplayName}` : undefined}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={config.label}
                          size="small"
                          color={config.color}
                          variant="outlined"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Share code copied to clipboard"
      />
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={4000}
        onClose={() => setErrorSnackbar(null)}
      >
        <Alert onClose={() => setErrorSnackbar(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </>
  )
}
